// Phase 34c.3 — Unified "All Partnerships" page (/partnerships).
//
// Sibling to /investor, different perspective:
//   /investor      → LP cockpit (member-only view, distribution history)
//   /partnerships  → Every partnership I'm involved in (sponsor OR LP)
//                    with role badge, role-specific stats.
//
// Data shape:
//   GET /api/syndication/partnerships
//     → list of {id, deal_id, sponsor_user_id, name, status,
//                total_equity_target, members_count, capital_calls_count,
//                created_at, updated_at}
//     Returns rows where the caller is sponsor OR member — already the
//     correct union per the backend.
//   GET /api/syndication/me/partnerships
//     → LP-side join with the per-member stats we need for the LP card:
//       my_ownership_pct, my_committed_amount, my_cumulative_pref_paid,
//       my_cumulative_capital_returned, deal_address, sponsor_name.
//
// Client-side join: build a map keyed by partnership_id from /me, then
// enrich each row from /partnerships. Sponsor-role rows won't have a /me
// entry (sponsors are excluded from /me/partnerships by design) — they
// keep just the listing fields, which is enough to render the sponsor card.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  MapPin,
  ArrowUpRight,
  Search,
  Users2,
  Wallet,
  Sparkles,
  BookOpen,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import Skeleton from "@/components/ui/Skeleton";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import { useAuth } from "@/contexts/AuthContext";

const FILTERS = [
  { id: "all",     label: "All",         testid: "partnerships-filter-all" },
  { id: "sponsor", label: "I sponsor",   testid: "partnerships-filter-sponsor" },
  { id: "lp",      label: "I'm an LP",   testid: "partnerships-filter-lp" },
];

function StatusPill({ status }) {
  const palette = {
    draft:  "bg-slate-100 text-slate-700",
    open:   "bg-amber-50 text-amber-700",
    funded: "bg-indigo-50 text-indigo-700",
    closed: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${palette[status] || palette.draft}`}>
      {(status || "draft").toUpperCase()}
    </span>
  );
}

function RoleBadge({ role, partnershipId }) {
  const palette =
    role === "sponsor"
      ? "bg-indigo-600 text-white"
      : "bg-slate-100 text-slate-700 ring-1 ring-slate-200";
  return (
    <span
      data-testid={`partnerships-card-role-badge-${partnershipId}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${palette}`}
    >
      {role === "sponsor" ? "Sponsor" : "LP"}
    </span>
  );
}

function PartnershipCard({ row, fmt }) {
  const isSponsor = row.role === "sponsor";
  return (
    <Link
      to={`/syndication/partnerships/${row.id}`}
      data-testid={`partnerships-card-${row.id}`}
      className="group relative flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-indigo-100"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={row.status} />
            <RoleBadge role={row.role} partnershipId={row.id} />
          </div>
          <h3 className="mt-2 truncate text-base font-semibold text-slate-900">
            {row.name || "Partnership"}
          </h3>
          {row.deal_address ? (
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{row.deal_address}</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Role-specific stat block */}
      {isSponsor ? (
        <dl className="grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Members</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">
              {row.members_count ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Capital calls</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">
              {row.capital_calls_count ?? 0}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Target raise</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">
              {fmt.money(row.total_equity_target)}
            </dd>
          </div>
        </dl>
      ) : (
        <dl className="grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">My ownership</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">
              {Number(row.my_ownership_pct || 0).toFixed(2)}%
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">My committed</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">
              {fmt.money(row.my_committed_amount)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">My total received</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-emerald-700">
              {fmt.money(
                Number(row.my_cumulative_pref_paid || 0) +
                  Number(row.my_cumulative_capital_returned || 0),
              )}
            </dd>
          </div>
        </dl>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
        <span>
          Created{" "}
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString(undefined, {
                month: "short", day: "numeric", year: "numeric",
              })
            : "—"}
        </span>
        <span className="inline-flex items-center gap-1 font-medium text-indigo-600 transition-transform group-hover:translate-x-0.5">
          Open <ArrowUpRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <Skeleton className="h-4 w-20 bg-slate-100" />
          <Skeleton className="mt-3 h-5 w-3/4 bg-slate-100" />
          <Skeleton className="mt-1 h-3 w-1/2 bg-slate-100" />
          <Skeleton className="mt-6 h-4 w-2/3 bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
      <div className="rounded-full bg-indigo-50 p-4">
        <Briefcase className="h-7 w-7 text-indigo-600" />
      </div>
      <h3 className="mt-5 text-base font-semibold text-slate-900">
        No partnerships yet
      </h3>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        Partnerships show up here whether you create one as a sponsor or
        you&rsquo;re invited in as an LP. Two ways to get started:
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Create one from a deal
        </Link>
        <a
          href="https://runidea.ai/docs/syndication"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <BookOpen className="h-3.5 w-3.5" />
          Learn how syndication works
        </a>
      </div>
    </div>
  );
}

export default function AllPartnerships() {
  const fmt = useFmt();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [listResp, meResp] = await Promise.all([
          api.get("/syndication/partnerships"),
          api.get("/syndication/me/partnerships").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        const meById = new Map();
        for (const r of meResp.data || []) {
          meById.set(r.partnership_id, r);
        }

        const enriched = (listResp.data || []).map((p) => {
          const meRow = meById.get(p.id);
          const isSponsor = user && p.sponsor_user_id === user.id;
          return {
            ...p,
            role: isSponsor ? "sponsor" : "lp",
            // Joined-only fields. Sponsor rows won't have these unless
            // the user is ALSO somehow in /me (shouldn't happen — /me
            // explicitly excludes sponsor rows — but we guard anyway).
            deal_address: meRow?.deal_address || null,
            sponsor_name: meRow?.sponsor_name || null,
            my_ownership_pct: meRow?.my_ownership_pct,
            my_committed_amount: meRow?.my_committed_amount,
            my_cumulative_pref_paid: meRow?.my_cumulative_pref_paid,
            my_cumulative_capital_returned: meRow?.my_cumulative_capital_returned,
          };
        });
        // Newest first by created_at.
        enriched.sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          return tb - ta;
        });
        setRows(enriched);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Could not load partnerships");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const filtered = useMemo(() => {
    let out = rows;
    if (filter === "sponsor") out = out.filter((r) => r.role === "sponsor");
    else if (filter === "lp") out = out.filter((r) => r.role === "lp");
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) => {
        const name = (r.name || "").toLowerCase();
        const addr = (r.deal_address || "").toLowerCase();
        return name.includes(q) || addr.includes(q);
      });
    }
    return out;
  }, [rows, filter, search]);

  const counts = useMemo(() => {
    const sponsor = rows.filter((r) => r.role === "sponsor").length;
    const lp = rows.filter((r) => r.role === "lp").length;
    return { all: rows.length, sponsor, lp };
  }, [rows]);

  return (
    <AppShell>
      <div data-testid="partnerships-page" className="space-y-7">
        <header>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">
            Partnerships
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Every partnership you&rsquo;re part of
          </h1>
          <p className="mt-1 max-w-xl text-sm text-slate-500">
            Sponsor and LP roles in one place. Switch perspective with the
            filter chips below.
          </p>
        </header>

        {/* Filter chips + search */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
            {FILTERS.map((f) => {
              const active = filter === f.id;
              const count = counts[f.id];
              return (
                <button
                  key={f.id}
                  type="button"
                  data-testid={f.testid}
                  onClick={() => setFilter(f.id)}
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {f.label}
                  <span className={`rounded-full px-1.5 text-[10px] font-semibold tabular-nums ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="partnerships-search-input"
              placeholder="Search by name or deal address…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <GridSkeleton />
        ) : filtered.length === 0 ? (
          rows.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500">
              No partnerships match &ldquo;<span className="font-medium text-slate-700">{search}</span>&rdquo; in this view.
            </div>
          )
        ) : (
          <div data-testid="partnerships-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((row) => (
              <PartnershipCard key={row.id} row={row} fmt={fmt} />
            ))}
          </div>
        )}

        {/* Footer hint */}
        {!loading && rows.length > 0 ? (
          <div className="flex items-center justify-between border-t border-slate-100 pt-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Users2 className="h-3.5 w-3.5" />
              {counts.sponsor} sponsored &middot; {counts.lp} as LP
            </span>
            <Link to="/investor" className="inline-flex items-center gap-1 font-medium text-indigo-600 hover:text-indigo-700">
              <Wallet className="h-3.5 w-3.5" /> Investor Portal
            </Link>
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
